/**
 * Mock Data for Local Development
 *
 * Comprehensive sample data for visual testing of the Internal UI
 * without deployed AWS resources. Only used when Cognito is NOT configured
 * (i.e. VITE_COGNITO_USER_POOL_ID and VITE_COGNITO_CLIENT_ID are absent).
 *
 * IMPORTANT: This data is NEVER used when real AWS resources are deployed.
 */

import type { Job, Project, UserProfile, Notification, ActivityChartDataPoint } from '../types';

const NOW = Date.now();
const HOUR = 3_600_000;
const DAY = 86_400_000;

// ── Jobs ──────────────────────────────────────────────────────────────────────

export const MOCK_JOBS: Job[] = [
    {
        job_id: 'mock-job-001',
        user_id: 'dev-local-001',
        title: '三体 · 第一章 · 疯狂年代',
        author: '刘慈欣',
        publisher: '重庆出版社',
        chapter: '第一章',
        created_timestamp: NOW - 2 * HOUR,
        updated_timestamp: NOW - 30 * 60_000,
        job_status: 'completed',
        job_progress: 100,
        input_s3_path: 's3://mock-bucket/mock-job-001/input.txt',
        output_s3_path: 's3://mock-bucket/mock-job-001/output.mp3',
        youtube_upload_status: 'completed',
        youtube_url: 'https://youtube.com/watch?v=mock123',
        tts_mode: 'custom_voice',
        speaker: 'Vivian',
        language: 'Chinese',
        is_favorite: true,
        project_id: 'mock-proj-001',
    },
    {
        job_id: 'mock-job-002',
        user_id: 'dev-local-001',
        title: 'The Great Gatsby — Chapter 1',
        author: 'F. Scott Fitzgerald',
        chapter: 'Chapter 1',
        created_timestamp: NOW - 15 * 60_000,
        updated_timestamp: NOW - 2 * 60_000,
        job_status: 'in_progress',
        job_progress: 62,
        input_s3_path: 's3://mock-bucket/mock-job-002/input.txt',
        tts_mode: 'voice_design',
        language: 'English',
        instruct: 'A warm, authoritative male narrator voice with a classic American accent.',
        project_id: 'mock-proj-001',
    },
    {
        job_id: 'mock-job-003',
        user_id: 'dev-local-001',
        title: '吾輩は猫である — 冒頭',
        author: '夏目漱石',
        created_timestamp: NOW - 5 * 60_000,
        updated_timestamp: NOW - 5 * 60_000,
        job_status: 'queued',
        job_progress: 0,
        input_s3_path: 's3://mock-bucket/mock-job-003/input.txt',
        tts_mode: 'custom_voice',
        speaker: 'Ono_Anna',
        language: 'Japanese',
        project_id: 'mock-proj-002',
    },
    {
        job_id: 'mock-job-004',
        user_id: 'dev-local-001',
        title: 'Voice Clone Demo — Shakespeare Sonnet 18',
        author: 'William Shakespeare',
        created_timestamp: NOW - 5 * HOUR,
        updated_timestamp: NOW - 4.5 * HOUR,
        job_status: 'failed',
        job_progress: 28,
        input_s3_path: 's3://mock-bucket/mock-job-004/input.txt',
        error_message: 'Reference audio too short: minimum 3 seconds required (got 1.2s)',
        tts_mode: 'voice_clone',
        language: 'English',
        ref_audio_s3_path: 's3://mock-bucket/mock-job-004/ref.wav',
        ref_text: 'Shall I compare thee to a summer\'s day?',
    },
    {
        job_id: 'mock-job-005',
        user_id: 'dev-local-001',
        title: '어린 왕자 — 1장',
        author: '앙투안 드 생텍쥐페리',
        chapter: '1장',
        created_timestamp: NOW - 1 * DAY,
        updated_timestamp: NOW - 23 * HOUR,
        job_status: 'completed',
        job_progress: 100,
        input_s3_path: 's3://mock-bucket/mock-job-005/input.txt',
        output_s3_path: 's3://mock-bucket/mock-job-005/output.mp3',
        youtube_upload_status: 'not_uploaded',
        tts_mode: 'custom_voice',
        speaker: 'Sohee',
        language: 'Korean',
        is_favorite: true,
        project_id: 'mock-proj-002',
    },
    {
        job_id: 'mock-job-006',
        user_id: 'dev-local-001',
        title: 'Der Prozess — Kapitel 1',
        author: 'Franz Kafka',
        chapter: 'Kapitel 1',
        created_timestamp: NOW - 3 * DAY,
        updated_timestamp: NOW - 2.9 * DAY,
        job_status: 'completed',
        job_progress: 100,
        input_s3_path: 's3://mock-bucket/mock-job-006/input.txt',
        output_s3_path: 's3://mock-bucket/mock-job-006/output.mp3',
        youtube_upload_status: 'uploading',
        youtube_upload_progress: 45,
        tts_mode: 'voice_design',
        language: 'German',
        instruct: 'Ein tiefer, nachdenklicher männlicher Erzähler mit ruhigem Tempo.',
    },
    {
        job_id: 'mock-job-007',
        user_id: 'dev-local-001',
        title: 'Le Petit Prince — Chapitre 2',
        author: 'Antoine de Saint-Exupéry',
        chapter: 'Chapitre 2',
        created_timestamp: NOW - 2 * DAY,
        updated_timestamp: NOW - 1.9 * DAY,
        job_status: 'completed',
        job_progress: 100,
        input_s3_path: 's3://mock-bucket/mock-job-007/input.txt',
        output_s3_path: 's3://mock-bucket/mock-job-007/output.mp3',
        youtube_upload_status: 'not_uploaded',
        tts_mode: 'custom_voice',
        speaker: 'Serena',
        language: 'French',
        project_id: 'mock-proj-003',
    },
    {
        job_id: 'mock-job-008',
        user_id: 'dev-local-001',
        title: 'Custom Voice — Ryan English Demo',
        author: 'Demo',
        created_timestamp: NOW - 10 * 60_000,
        updated_timestamp: NOW - 8 * 60_000,
        job_status: 'in_progress',
        job_progress: 15,
        input_s3_path: 's3://mock-bucket/mock-job-008/input.txt',
        tts_mode: 'custom_voice',
        speaker: 'Ryan',
        language: 'English',
        instruct: 'Very excited and energetic.',
    },
    {
        job_id: 'mock-job-009',
        user_id: 'dev-local-001',
        title: 'Война и мир — Глава 1',
        author: 'Лев Толстой',
        chapter: 'Глава 1',
        created_timestamp: NOW - 4 * DAY,
        updated_timestamp: NOW - 3.8 * DAY,
        job_status: 'completed',
        job_progress: 100,
        input_s3_path: 's3://mock-bucket/mock-job-009/input.txt',
        output_s3_path: 's3://mock-bucket/mock-job-009/output.mp3',
        youtube_upload_status: 'failed',
        tts_mode: 'voice_design',
        language: 'Russian',
        instruct: 'Глубокий мужской голос с размеренным темпом повествования.',
    },
    {
        job_id: 'mock-job-010',
        user_id: 'dev-local-001',
        title: 'Interrupted Spot Instance Job',
        author: 'System Test',
        created_timestamp: NOW - 6 * HOUR,
        updated_timestamp: NOW - 5.5 * HOUR,
        job_status: 'interrupted',
        job_progress: 73,
        input_s3_path: 's3://mock-bucket/mock-job-010/input.txt',
        error_message: 'EC2 Spot instance reclaimed — job will be retried automatically.',
        tts_mode: 'custom_voice',
        speaker: 'Aiden',
        language: 'English',
    },
];

// ── Projects ──────────────────────────────────────────────────────────────────

export const MOCK_PROJECTS: Project[] = [
    {
        project_id: 'mock-proj-001',
        user_id: 'dev-local-001',
        project_name: 'Audiobook Collection',
        description: 'Classic novels converted to audiobooks using various TTS modes.',
        created_timestamp: NOW - 7 * DAY,
        updated_timestamp: NOW - 2 * HOUR,
        is_favorite: true,
    },
    {
        project_id: 'mock-proj-002',
        user_id: 'dev-local-001',
        project_name: 'Asian Literature',
        description: 'Japanese and Korean literature TTS experiments.',
        created_timestamp: NOW - 5 * DAY,
        updated_timestamp: NOW - 1 * DAY,
        is_favorite: false,
    },
    {
        project_id: 'mock-proj-003',
        user_id: 'dev-local-001',
        project_name: 'European Classics',
        description: 'French, German, and Russian literature narration.',
        created_timestamp: NOW - 4 * DAY,
        updated_timestamp: NOW - 2 * DAY,
        is_favorite: false,
    },
];

// ── User Profile ──────────────────────────────────────────────────────────────

export const MOCK_USER_PROFILE: UserProfile = {
    user_id: 'dev-local-001',
    credits_remaining: 42,
    total_credits: 100,
    credits_used: 258,
    tier: 'Bronze',
    display_name: 'Dev User',
    email: 'dev@localhost',
    created_timestamp: NOW - 30 * DAY,
    updated_timestamp: NOW - 1 * HOUR,
    welcome_dismissed: true,
};

// ── Notifications ─────────────────────────────────────────────────────────────

export const MOCK_NOTIFICATIONS: Notification[] = [
    {
        notification_id: 'mock-notif-001',
        user_id: 'dev-local-001',
        message: '"三体 · 第一章" has finished processing. Audio is ready for download.',
        message_type: 'job_completed',
        read: false,
        created_timestamp: NOW - 30 * 60_000,
        metadata: { title: 'Job Completed', job_id: 'mock-job-001' },
    },
    {
        notification_id: 'mock-notif-002',
        user_id: 'dev-local-001',
        message: '"Voice Clone Demo" failed: Reference audio too short.',
        message_type: 'job_failed',
        read: false,
        created_timestamp: NOW - 4.5 * HOUR,
        metadata: { title: 'Job Failed', job_id: 'mock-job-004' },
    },
    {
        notification_id: 'mock-notif-003',
        user_id: 'dev-local-001',
        message: '"三体 · 第一章" has been uploaded to YouTube.',
        message_type: 'job_completed',
        read: true,
        created_timestamp: NOW - 1 * HOUR,
        read_timestamp: NOW - 50 * 60_000,
        metadata: { title: 'YouTube Upload Complete', job_id: 'mock-job-001' },
    },
    {
        notification_id: 'mock-notif-004',
        user_id: 'dev-local-001',
        message: '"Interrupted Spot Instance Job" was interrupted. It will be retried automatically.',
        message_type: 'job_failed',
        read: false,
        created_timestamp: NOW - 5.5 * HOUR,
        metadata: { title: 'Spot Interruption', job_id: 'mock-job-010' },
    },
    {
        notification_id: 'mock-notif-005',
        user_id: 'dev-local-001',
        message: 'You have 42 credits remaining. Consider upgrading your plan.',
        message_type: 'promotion',
        read: true,
        created_timestamp: NOW - 2 * DAY,
        read_timestamp: NOW - 1.5 * DAY,
        metadata: { title: 'Credits Low' },
    },
];

// ── Activity Chart Data (last 7 days) ─────────────────────────────────────────

function buildActivityData(): ActivityChartDataPoint[] {
    const points: ActivityChartDataPoint[] = [];
    for (let i = 6; i >= 0; i--) {
        const d = new Date(NOW - i * DAY);
        const label = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        // Simulate varying activity
        const counts = [1, 0, 2, 1, 3, 0, 2];
        const credits = [1, 0, 2, 1, 3, 0, 2];
        points.push({ date: label, count: counts[6 - i], creditsUsed: credits[6 - i] });
    }
    return points;
}

export const MOCK_ACTIVITY_DATA: ActivityChartDataPoint[] = buildActivityData();
